/**
 * The subject form's draft — the ONE form behind "Thêm chủ thể", "Sửa chủ thể" and the primary
 * subject of "Thêm khách hàng" (a customer is an account plus its primary subject, ADR-022):
 * what staff type, the checks made before sending (the same rules and codes as the server,
 * subsystems/identity/facade/adapters/admin-api/src/admin/subject_write.rs) and the request body (`SubjectInput`, the body of
 * `POST /subjects` and the `subject` of `POST /customers`).
 *
 * Pure functions (no React): `npm test` covers them. Names: ./subjectNames.ts; tax codes and
 * CCCD: ./taxCode.ts; postal codes: ./customerProfile.ts (a Vietnamese address without one is
 * stored with the national default, 76000).
 */

import type { SubjectFieldError, SubjectGender, SubjectIdentifierType, SubjectInput, SubjectProfile } from '../api/areas/accounts';
import { fullName } from './subjectNames.ts';
import { checkCccd, normalizeTaxCode } from './taxCode.ts';

/** Cá nhân / Tổ chức. */
export type SubjectKind = 'INDIVIDUAL' | 'ORGANIZATION';
/** An organisation: an enterprise / organisation, or a household business (hộ kinh doanh). */
export type OrgKind = 'ENTERPRISE' | 'HOUSEHOLD_BUSINESS';
/** The legal representative: keep (edit), an existing individual subject, a new one, or (a
 * household business) its owner as entered above. */
export type RepMode = 'keep' | 'existing' | 'new' | 'owner';

/** A Vietnamese address without a postal code is stored with this one (owner decision
 * 2026-09-30; the server's `identity_subjects::vn::DEFAULT_POSTAL_CODE`). */
export const DEFAULT_POSTAL_CODE = '76000';

/** The gender select's options (the server's `identity_subjects::vn::Gender`). A stored value
 * outside them (e.g. a label an earlier demo seed wrote) loads as no choice: never sent as is. */
export const GENDERS: readonly SubjectGender[] = ['Male', 'Female'];

/** An organisation's legal representative entered inline (an individual subject). */
export interface RepDraft {
  firstName: string;
  lastName: string;
  position: string;
  birthDate: string;
  gender: SubjectGender | '';
  cccd: string;
  nationality: string;
  email: string;
  phone: string;
  sameAddress: boolean;
  street: string;
  provinceId: string;
  wardId: string;
  postalCode: string;
}

export interface SubjectDraft {
  kind: SubjectKind;
  orgKind: OrgKind;
  customerId: string;
  /** An organisation's name. */
  legalName: string;
  /** An individual's own first / last name; an organisation's contact person's; a household
   * business's owner's. */
  firstName: string;
  lastName: string;
  tradeName: string;
  establishDate: string;
  nationality: string;
  /** Individuals and household business owners. */
  birthDate: string;
  gender: SubjectGender | '';
  position: string;
  idType: SubjectIdentifierType;
  idValue: string;
  idIssuedAt: string;
  /** Household business: Mã số đăng ký hộ kinh doanh; its tax code (optional: the owner's CCCD). */
  householdCode: string;
  householdTaxCode: string;
  country: string;
  street: string;
  provinceId: string;
  wardId: string;
  city: string;
  state: string;
  postalCode: string;
  email: string;
  phone: string;
  fax: string;
  reason: string;
  repMode: RepMode;
  repSubjectId: string;
  rep: RepDraft;
}

export const blankRep = (): RepDraft => ({
  firstName: '',
  lastName: '',
  position: '',
  birthDate: '',
  gender: '',
  cccd: '',
  nationality: 'VN',
  email: '',
  phone: '',
  sameAddress: true,
  street: '',
  provinceId: '',
  wardId: '',
  postalCode: '',
});

export function blankSubject(kind: SubjectKind = 'INDIVIDUAL', customerId = '', orgKind: OrgKind = 'ENTERPRISE'): SubjectDraft {
  return {
    kind,
    orgKind,
    customerId,
    legalName: '',
    firstName: '',
    lastName: '',
    tradeName: '',
    establishDate: '',
    nationality: 'VN',
    birthDate: '',
    gender: '',
    position: '',
    idType: defaultIdType(kind, orgKind),
    idValue: '',
    idIssuedAt: '',
    householdCode: '',
    householdTaxCode: '',
    country: 'VN',
    street: '',
    provinceId: '',
    wardId: '',
    city: '',
    state: '',
    postalCode: '',
    email: '',
    phone: '',
    fax: '',
    reason: '',
    repMode: kind === 'ORGANIZATION' ? (orgKind === 'HOUSEHOLD_BUSINESS' ? 'owner' : 'new') : 'keep',
    repSubjectId: '',
    rep: blankRep(),
  };
}

/** A person's document (individual, household owner) or an organisation's code. */
export function defaultIdType(kind: SubjectKind, orgKind: OrgKind): SubjectIdentifierType {
  return personal(kind, orgKind) ? 'NATIONAL_ID' : 'TAX_CODE';
}

/** The identity data are a person's: an individual, or a household business (its owner). */
export function personal(kind: SubjectKind, orgKind: OrgKind): boolean {
  return kind === 'INDIVIDUAL' || orgKind === 'HOUSEHOLD_BUSINESS';
}

export const isHousehold = (d: Pick<SubjectDraft, 'kind' | 'orgKind'>) => d.kind === 'ORGANIZATION' && d.orgKind === 'HOUSEHOLD_BUSINESS';

/** The subject type sent. */
export function subjectType(d: Pick<SubjectDraft, 'kind' | 'orgKind'>): 'INDIVIDUAL' | 'ORGANIZATION' | 'HOUSEHOLD_BUSINESS' {
  if (d.kind === 'INDIVIDUAL') return 'INDIVIDUAL';
  return d.orgKind === 'HOUSEHOLD_BUSINESS' ? 'HOUSEHOLD_BUSINESS' : 'ORGANIZATION';
}

/** Switching Cá nhân / Tổ chức or the organisation type: what else changes with it. */
export function kindPatch(kind: SubjectKind, orgKind: OrgKind): Partial<SubjectDraft> {
  return {
    kind,
    orgKind,
    idType: defaultIdType(kind, orgKind),
    repMode: kind === 'ORGANIZATION' ? (orgKind === 'HOUSEHOLD_BUSINESS' ? 'owner' : 'new') : 'keep',
  };
}

/** The draft of a stored subject (identity numbers and the birth date stay empty: the ones on
 * file are kept). */
export function subjectFromProfile(p: SubjectProfile): SubjectDraft {
  const kind: SubjectKind = p.subject_type === 'INDIVIDUAL' ? 'INDIVIDUAL' : 'ORGANIZATION';
  const orgKind: OrgKind = p.subject_type === 'HOUSEHOLD_BUSINESS' ? 'HOUSEHOLD_BUSINESS' : 'ENTERPRISE';
  return {
    ...blankSubject(kind, p.managing_customer_id ?? '', orgKind),
    legalName: p.legal_name,
    firstName: p.first_name ?? '',
    lastName: p.last_name ?? '',
    tradeName: p.trade_name ?? '',
    establishDate: p.establish_date ?? '',
    nationality: p.nationality ?? 'VN',
    gender: GENDERS.find((g) => g === p.gender) ?? '',
    position: p.position ?? '',
    country: p.address?.country_code ?? p.country_code,
    street: p.vn_address?.address ?? p.address?.street[0] ?? '',
    provinceId: p.vn_address ? String(p.vn_address.province_id) : '',
    wardId: p.vn_address?.ward_id ? String(p.vn_address.ward_id) : '',
    city: p.address?.city ?? '',
    state: p.address?.province ?? '',
    postalCode: p.address?.postal_code ?? '',
    email: p.email ?? '',
    phone: p.phone ?? '',
    fax: p.fax ?? '',
    repMode: 'keep',
  };
}

const opt = (v: string) => (v.trim() ? v.trim() : null);
const compact = (v: string) => v.replace(/\s+/g, '');

/**
 * A postal code as the server accepts it: Vietnam the 5-digit national postal code (6 in the
 * former system), elsewhere 2–12 letters, digits, spaces or hyphens (empty is allowed: in Vietnam
 * the default 76000 is stored).
 */
export function postalCodeOk(country: string, value: string): boolean {
  const v = value.trim();
  if (!v) return true;
  return country.toUpperCase() === 'VN' ? /^\d{5,6}$/.test(v) : /^[A-Za-z0-9 -]{2,12}$/.test(v);
}

/** An e-mail as the server accepts it. */
export function emailOk(v: string): boolean {
  const e = v.trim();
  const at = e.indexOf('@');
  if (at < 1 || e.indexOf('@', at + 1) !== -1 || /\s/.test(e) || e.length > 254) return false;
  const domain = e.slice(at + 1);
  return domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.');
}

/**
 * A subject's phone as the server accepts it (`identity_subjects::vn::normalize_phone`): the EPP
 * form `+CC.number` (4–14 digits), or a Vietnamese mobile number typed nationally or with 84
 * (`0912 345 678`, `+84 912 345 678`).
 */
export function subjectPhoneOk(value: string): boolean {
  const t = value.trim();
  const epp = /^\+(\d+)\.(.*)$/.exec(t);
  if (epp) {
    const n = (epp[2] ?? '').replace(/\D/g, '');
    return n.length >= 4 && n.length <= 14;
  }
  const digits = t.replace(/\D/g, '');
  const national = digits.startsWith('84') && (t.startsWith('+') || digits.length === 11) ? digits.slice(2) : digits.startsWith('0') ? digits.slice(1) : null;
  return national !== null && national.length === 9 && !national.startsWith('0');
}

/** The organisation's MST as stored (13 digits typed become 10-3). */
export function orgTaxCode(d: Pick<SubjectDraft, 'idValue'>): string {
  const r = normalizeTaxCode('ORGANIZATION', d.idValue);
  return r.ok ? r.value : d.idValue;
}

function repInput(d: SubjectDraft): SubjectInput {
  const r = d.rep;
  const vn = d.country.toUpperCase() === 'VN';
  const same = r.sameAddress;
  return {
    legal_name: fullName(r.lastName, r.firstName),
    first_name: opt(r.firstName),
    last_name: opt(r.lastName),
    nationality: opt(r.nationality),
    birth_date: r.birthDate || null,
    gender: r.gender || null,
    position: opt(r.position),
    identifier: r.cccd.trim() ? { type: r.nationality.toUpperCase() === 'VN' ? 'NATIONAL_ID' : 'PASSPORT', value: r.cccd } : null,
    address: {
      country_code: d.country,
      street: same ? d.street : r.street,
      province_id: vn ? Number(same ? d.provinceId : r.provinceId) || null : null,
      ward_id: vn ? Number(same ? d.wardId : r.wardId) || null : null,
      city: vn ? null : opt(d.city),
      state: vn ? null : opt(d.state),
      postal_code: opt(same ? d.postalCode : r.postalCode),
    },
    email: r.email,
    phone: r.phone,
  };
}

/** A household business's owner as its legal representative (the same person, same address). */
function ownerAsRep(d: SubjectDraft): SubjectInput {
  const vn = d.country.toUpperCase() === 'VN';
  return {
    legal_name: fullName(d.lastName, d.firstName),
    first_name: opt(d.firstName),
    last_name: opt(d.lastName),
    nationality: opt(d.nationality),
    birth_date: d.birthDate || null,
    gender: d.gender || null,
    position: opt(d.position) ?? 'Chủ hộ kinh doanh',
    identifier: d.idValue.trim() ? { type: d.idType, value: compact(d.idValue), issued_at: opt(d.idIssuedAt) } : null,
    address: {
      country_code: d.country,
      street: d.street,
      province_id: vn && d.provinceId ? Number(d.provinceId) : null,
      ward_id: vn && d.wardId ? Number(d.wardId) : null,
      city: vn ? null : opt(d.city),
      state: vn ? null : opt(d.state),
      postal_code: opt(d.postalCode),
    },
    email: d.email,
    phone: d.phone,
  };
}

/**
 * The checks made before sending — the same rules and codes as the server, so both screens show
 * the same messages. `editing`: identity number and birth date may be left empty (kept on file);
 * `kept`: a document number is on file.
 */
export function subjectErrors(d: SubjectDraft, editing = false, kept = false): SubjectFieldError[] {
  const out: SubjectFieldError[] = [];
  const add = (field: string, code: string) => out.push({ field, code });
  const person = personal(d.kind, d.orgKind);
  const household = isHousehold(d);
  // Every person has a first and a last name (an organisation: its contact person).
  if (!d.lastName.trim()) add('last_name', 'REQUIRED');
  if (!d.firstName.trim()) add('first_name', 'REQUIRED');
  if (d.kind === 'ORGANIZATION' && !d.legalName.trim()) add('legal_name', 'REQUIRED');
  if (person) {
    if (!d.birthDate && !editing) add('birth_date', 'REQUIRED');
    if (!d.gender) add('gender', 'REQUIRED');
  }
  if (d.idValue.trim()) {
    if (!person) {
      const r = normalizeTaxCode('ORGANIZATION', d.idValue);
      if (!r.ok) add('identifier.value', r.code);
    } else if (d.idType === 'NATIONAL_ID' && d.nationality.toUpperCase() === 'VN') {
      for (const code of checkCccd(d.idValue, d.birthDate, d.gender)) add('identifier.value', code);
    } else if (d.idType === 'PASSPORT' && !/^.{6,20}$/.test(compact(d.idValue))) {
      add('identifier.value', 'PASSPORT_FORMAT');
    }
  } else if (!kept) {
    add('identifier.value', 'REQUIRED');
  }
  if (household) {
    const code = compact(d.householdCode);
    if (!code && !editing) add('household_code', 'REQUIRED');
    else if (code && !/^[A-Za-z0-9-]{6,20}$/.test(code)) add('household_code', 'HOUSEHOLD_CODE_FORMAT');
    const tax = compact(d.householdTaxCode);
    if (tax) {
      const r = normalizeTaxCode('HOUSEHOLD_BUSINESS', tax);
      if (!r.ok) add('household_tax_code', r.code);
      else if (d.idType === 'NATIONAL_ID' && compact(d.idValue) && compact(d.idValue) !== r.value) add('household_tax_code', 'TAX_CODE_NOT_OWNER_CCCD');
    }
  }
  const vn = d.country.toUpperCase() === 'VN';
  if (!/^[A-Za-z]{2}$/.test(d.country.trim())) add('address.country_code', 'REQUIRED');
  else if (vn) {
    if (!d.provinceId) add('address.province_id', 'REQUIRED');
    if (!d.wardId) add('address.ward_id', 'REQUIRED');
    if (d.provinceId && d.wardId && !d.street.trim()) add('address.street', 'STREET_REQUIRED');
  } else {
    if (!d.street.trim()) add('address.street', 'REQUIRED');
    if (!d.city.trim()) add('address.city', 'REQUIRED');
  }
  if (d.postalCode.trim() && !postalCodeOk(d.country, d.postalCode)) add('address.postal_code', 'POSTAL_CODE_FORMAT');
  if (!emailOk(d.email)) add('email', 'EMAIL_FORMAT');
  if (!subjectPhoneOk(d.phone)) add('phone', 'PHONE_FORMAT');
  if (d.kind === 'ORGANIZATION' && d.repMode === 'new' && !d.rep.sameAddress && d.rep.postalCode.trim() && !postalCodeOk(d.country, d.rep.postalCode)) {
    add('representative.new.address.postal_code', 'POSTAL_CODE_FORMAT');
  }
  if (d.kind === 'ORGANIZATION' && d.repMode === 'existing' && !d.repSubjectId) add('representative.subject_id', 'REQUIRED');
  if (d.kind === 'ORGANIZATION' && d.repMode === 'new') {
    const f = (x: string) => `representative.new.${x}`;
    if (!d.rep.lastName.trim()) add(f('last_name'), 'REQUIRED');
    if (!d.rep.firstName.trim()) add(f('first_name'), 'REQUIRED');
    if (!d.rep.birthDate) add(f('birth_date'), 'REQUIRED');
    if (!d.rep.gender) add(f('gender'), 'REQUIRED');
    if (!d.rep.cccd.trim()) add(f('identifier.value'), 'REQUIRED');
    else if (d.rep.nationality.toUpperCase() === 'VN') for (const code of checkCccd(d.rep.cccd, d.rep.birthDate, d.rep.gender)) add(f('identifier.value'), code);
    if (!emailOk(d.rep.email)) add(f('email'), 'EMAIL_FORMAT');
    if (!subjectPhoneOk(d.rep.phone)) add(f('phone'), 'PHONE_FORMAT');
    if (vn && !d.rep.sameAddress) {
      if (!d.rep.provinceId) add(f('address.province_id'), 'REQUIRED');
      if (!d.rep.wardId) add(f('address.ward_id'), 'REQUIRED');
    }
  }
  return out;
}

/** The body sent (`POST /subjects`, the `subject` of `POST /customers`, or a new version). */
export function subjectInput(d: SubjectDraft, editing = false, version?: number): SubjectInput {
  const vn = d.country.toUpperCase() === 'VN';
  const person = personal(d.kind, d.orgKind);
  const household = isHousehold(d);
  const body: SubjectInput = {
    legal_name: d.kind === 'INDIVIDUAL' ? fullName(d.lastName, d.firstName) : d.legalName.trim(),
    first_name: opt(d.firstName),
    last_name: opt(d.lastName),
    trade_name: d.kind === 'ORGANIZATION' ? opt(d.tradeName) : null,
    establish_date: d.kind === 'ORGANIZATION' ? opt(d.establishDate) : null,
    nationality: opt(d.nationality),
    gender: person && d.gender ? d.gender : null,
    position: opt(d.position),
    address: {
      country_code: d.country.toUpperCase(),
      street: d.street,
      province_id: vn && d.provinceId ? Number(d.provinceId) : null,
      ward_id: vn && d.wardId ? Number(d.wardId) : null,
      city: vn ? null : opt(d.city),
      state: vn ? null : opt(d.state),
      postal_code: opt(d.postalCode),
    },
    email: d.email.trim(),
    phone: d.phone.trim(),
    fax: opt(d.fax),
  };
  if (person && d.birthDate) body.birth_date = d.birthDate;
  if (d.idValue.trim()) body.identifier = { type: d.idType, value: person ? compact(d.idValue) : orgTaxCode(d), issued_at: opt(d.idIssuedAt) };
  if (household) {
    if (d.householdCode.trim()) body.household_code = compact(d.householdCode).toUpperCase();
    if (d.householdTaxCode.trim()) body.household_tax_code = compact(d.householdTaxCode);
  }
  if (d.kind === 'ORGANIZATION' && d.repMode === 'existing' && d.repSubjectId) body.representative = { subject_id: d.repSubjectId };
  if (d.kind === 'ORGANIZATION' && d.repMode === 'new') body.representative = { new: repInput(d) };
  if (household && d.repMode === 'owner' && !editing) body.representative = { new: ownerAsRep(d) };
  if (editing) {
    body.reason = d.reason;
    if (version !== undefined) body.expected_version = version;
  } else {
    body.subject_type = subjectType(d);
    body.managing_customer_id = d.customerId || undefined;
  }
  return body;
}

/** The subject's display name as the customer takes it (the account's name follows it). */
export function subjectDisplayName(d: SubjectDraft): string {
  return d.kind === 'INDIVIDUAL' ? fullName(d.lastName, d.firstName) : d.legalName.trim().replace(/\s+/g, ' ');
}

/** What the subject form's fields are called when they sit under `prefix` (the `subject.` of
 * "Thêm khách hàng"): the server's refusals for them, without the prefix. */
export function unprefixed(errors: SubjectFieldError[], prefix: string): SubjectFieldError[] {
  return errors.filter((e) => e.field.startsWith(prefix)).map((e) => ({ ...e, field: e.field.slice(prefix.length) }));
}
